/**
 * DragLayer — drag-sortable zone containers for the pipeline step list.
 *
 * Each zone gets its own DndContext to keep drag interactions scoped:
 *   - VizZone: input/output visualizer boxes (dashed border)
 *   - TransformStrip: the central scrollable transform strip
 *
 * Each zone uses SortableStepChip from ./SortableStepChip for rendering.
 */

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  useSensors,
} from '@dnd-kit/core';
import {
  horizontalListSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable';
import React from 'react';

import type { StepRenderHint } from '@/hooks/useVisualizerStepDetection';

import { SortableStepChip } from './SortableStepChip';

// ---------------------------------------------------------------------------
// VizZone — container for input/output visualizer boxes
// ---------------------------------------------------------------------------

export interface VizZoneProps {
  label: string;
  hints: StepRenderHint[];
  zoneType: 'head-viz' | 'tail-viz';
  selectedStepId: string | null;
  onSelectStep: (stepId: string) => void;
  onOpenSettings: (stepId: string) => void;
  removeTool: (stepId: string) => void;
  toggleToolEnabled: (stepId: string) => void;
  sensors: ReturnType<typeof useSensors>;
  onDragEnd: (event: DragEndEvent) => void;
}

export const VizZone: React.FC<VizZoneProps> = ({
  label, hints, zoneType, selectedStepId, onSelectStep, onOpenSettings,
  removeTool, toggleToolEnabled, sensors, onDragEnd,
}) => {
  if (hints.length === 0) return null;
  return (
    <div
      className="flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-dashed border-violet-400/20 bg-violet-500/[0.03] shrink-0"
      title={`${label} visualizers`}
    >
      <span className="text-[7px] text-violet-400/40 uppercase tracking-wider font-medium shrink-0 select-none">
        {label}
      </span>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={hints.map((h) => h.step.id)} strategy={horizontalListSortingStrategy}>
          {hints.map((hint, idx) => (
            <SortableStepChip
              key={hint.step.id}
              hint={hint}
              idx={idx}
              zoneType={zoneType}
              isSelected={hint.step.id === selectedStepId}
              onSelectStep={onSelectStep}
              onOpenSettings={onOpenSettings}
              removeTool={removeTool}
              toggleToolEnabled={toggleToolEnabled}
            />
          ))}
        </SortableContext>
      </DndContext>
    </div>
  );
};

// ---------------------------------------------------------------------------
// TransformStrip — central scrollable transform zone
// ---------------------------------------------------------------------------

export interface TransformStripProps {
  hints: StepRenderHint[];
  stepNumbers: (number | undefined)[];
  selectedStepId: string | null;
  onSelectStep: (stepId: string) => void;
  onOpenSettings: (stepId: string) => void;
  removeTool: (stepId: string) => void;
  toggleToolEnabled: (stepId: string) => void;
  sensors: ReturnType<typeof useSensors>;
  onDragEnd: (event: DragEndEvent) => void;
}

export const TransformStrip: React.FC<TransformStripProps> = ({
  hints, stepNumbers, selectedStepId, onSelectStep, onOpenSettings,
  removeTool, toggleToolEnabled, sensors, onDragEnd,
}) => {
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext
        items={hints.map((h) => h.step.id)}
        strategy={horizontalListSortingStrategy}
      >
        {hints.length > 0 ? (
          hints.map((hint, idx) => (
            <SortableStepChip
              key={hint.step.id}
              hint={hint}
              idx={idx}
              zoneType="transform"
              stepNumber={stepNumbers[idx]}
              isSelected={hint.step.id === selectedStepId}
              onSelectStep={onSelectStep}
              onOpenSettings={onOpenSettings}
              removeTool={removeTool}
              toggleToolEnabled={toggleToolEnabled}
            />
          ))
        ) : (
          <span className="text-[10px] text-muted-foreground/40 italic px-2 shrink-0 select-none">
            Add a tool
          </span>
        )}
      </SortableContext>
    </DndContext>
  );
};
